<template lang="pug">
article.overflow-hidden.the-comment(
  :lang="language"
  data-testid="the-comment"
)
  CommentDrawer(
    ref="menu"
    v-model:open="showInfoPanel"
    :nid="nid"
    :changed="changed"
    :notes="commentBody.notes"
    :contributions-by-role="contributionsByRole"
    :detail-level-weight="detailLevelWeight"
  )

  VBottomSheet(
    v-model="showBottomSheet"
    style="max-width: 700px; margin-left: auto; margin-right: auto"
  )
    VCard
      VCardText
        div
          strong.section-title-2 {{ bottomSheetNoteNumber }}
        CommentNote(
          v-if="bottomSheetNote"
          :note="bottomSheetNote"
        )
        div.d-flex
          VBtn.ml-auto.mt-2(
            variant="outlined"
            :title="i18n.t('TheComment.CloseNote')"
            :aria-label="i18n.t('TheComment.CloseNote')"
            @click="showBottomSheet = false"
          )
            VIcon {{ mdiMinus }}
            | {{ i18n.t("Global.close") }}

  header
    div.surface
      VContainer.pt-6
        slot(
          name="title"
          v-bind="{ breadcrumbs, pageTitle }"
        )
          VRow
            VCol
              VvBreadcrumbs.mb-4(:breadcrumbs="breadcrumbs")
              HH.text-h1 {{ pageTitle }}
      VContainer.pb-6(:fluid="true")
        VRow
          VCol.d-flex.align-center.overflow-x-auto.content-center-no-overlfow
            VBtn.mr-2.d-print-none.the-comment__back-to-list-button(
              v-if="parentCrumb"
              :to="localePath(parentCrumb.to)"
              variant="flat"
              color="primary-c700"
              :title="i18n.t('TheComment.BackToList')"
              @click="modalCloseHook"
            )
              VIcon {{ mdiArrowLeft }}
            VvShare(:title="documentTitle")
            VBtn.ml-2(
              :title="i18n.t('TheComment.InformationTitle')"
              variant="outlined"
              @click="showInfoPanel = !showInfoPanel"
            )
              VIcon.mr-2 {{ mdiInformationOutline }}
              span {{ i18n.t("TheComment.Information") }}

            ReadingListMenu(:comment-id="nid")
              template(#activator="activatorProps")
                VBtn.ml-2(
                  v-bind="activatorProps['props']"
                  variant="outlined"
                  :title="i18n.t('ReadingList.AddCommentReadingList')"
                )
                  VIcon.mr-2 {{ mdiPlaylistPlus }}
                  span {{ i18n.t("ReadingList.ReadingList") }}

            VMenu
              template(#activator="activatorProps")
                VBtn.ml-2(
                  v-bind="activatorProps['props']"
                  variant="outlined"
                  :disabled="languageListItems.length < 2"
                )
                  VIcon.mr-2 {{ mdiTranslate }}
                  span {{ localesByCode.get(language)?.name ?? language }}
              VList
                VListItem(
                  v-for="item in languageListItems"
                  :key="item.code"
                  :value="item.code"
                  :disabled="item.isCurrent"
                  :to="item.to"
                  :replace="true"
                  @click="(e) => modalLanguageHook(e, item)"
                )
                  VListItemTitle {{ item.name }}

    VContainer.pb-3.pt-3
      VRow
        VCol
          HSection
            CommentVerses(
              v-for="(vidRange, key) in ranges"
              :key="key"
              :vid-range="vidRange"
              :local-tid="localTid"
            )

  HSection.surface
    VContainer
      VRow
        VCol
          CommentBodyFull(
            :body-full-parts="commentBody.bodyFullParts"
            :body-summary="commentBody.bodySummary"
            :toc="commentBody.toc"
            @show-note="(n) => { bottomSheetNoteNumber = n; }"
          )

  footer.mt-6
    VContainer
      slot(
        name="footer-action"
        v-bind="{ previousComment, parentCrumb }"
      )
        VRow(v-if="!insideModal")
          VCol
            VBtn.the-comment__previous-button(
              v-if="previousComment"
              v-tooltip:top="previousComment.heading"
              variant="outlined"
              :to="localePath(getCommentLocation(previousComment.nid))"
            )
              | {{ i18n.t("TheComment.Previous") }}

          VCol.text-right
            VBtn.d-print-none.the-comment__back-to-list-button(
              v-if="parentCrumb"
              :color="dark ? 'primary-c800' : 'primary-c500'"
              :to="localePath(parentCrumb.to)"
              variant="flat"
            )
              VIcon.mr-2 {{ mdiArrowLeft }}
              span(:title="parentCrumb.title") {{ i18n.t("TheComment.BackToList") }}

      HSection.pt-3.pb-6(v-if="nextComment")
        HH.text-h2.section-title-2 {{ i18n.t("TheComment.NextComment") }}
        CommentTeaserCard.my-2.the-comment__next-comment-card(
          :heading="nextComment.heading"
          :teaser="nextComment.teaser"
          :ranges="nextComment.ranges"
          :detail-level-name="nextComment.detailLevel.name[i18n.locale.value] ?? '?'"
          :detail-level-num-dots="numDotsPerDetailLevelWeight[nextComment.detailLevel.weight] ?? 0"
          :nid="nextComment.nid"
          :local-tid="localTid"
          :commentary-type-machine-name="nextComment.commentaryType.machineName"
          :show-default-reading-list="true"
          :show-reading-list-menu="true"
          :language="nextComment.language"
        )

      HSection.pt-3.pb-3
        HH.text-h2.section-title-2 {{ i18n.t("TheComment.SimiliarTopic") }}
        CommentMoreLikeThis(
          :local-tid="localTid"
          :nid="nid"
        )
</template>

<script setup lang="ts">
import { makeRange, parseVidsString, type VidRange } from "~/units/scripture";
import { computed, inject, ref } from "vue";
import { useI18n } from "vue-i18n";
import type { RouteLocationGeneric } from "vue-router";
import {
  mdiArrowLeft,
  mdiInformationOutline,
  mdiMinus,
  mdiPlaylistPlus,
  mdiTranslate,
} from "@mdi/js";
import { prepareCommentBody } from "./commentBody.util";
import {
  computeCommentaryFilters,
  getCommentaryNameForBreadcrumb,
} from "./TheComment.util";
import type { CommentaryFilters } from "~/stores/useCommentaryFiltersStore";
import { injectRequired } from "~/injectRequired";
import { WindowKey } from "~/plugins/window/common";
import HSection from "~/headings/components/HSection.vue";
import HH from "~/headings/components/HH.vue";
import {
  getBibleLocationFromRange,
  getCommentaryLocationFromFilters,
  getCommentLocation,
  type SimpleNamedLocation,
} from "~/routing/locations";
import {
  InModalViewKey,
  LogFunctionKey,
  ScriptureClientKey,
} from "~/injectionKeys";
import type { Props } from "~/components/TheComment.types";
import { numDotsPerDetailLevelWeight } from "~/static/taxonomy/detailLevels";
import { locales, localesByCode } from "~/i18n/locales";
import { makeListString } from "~/units/strings";
import {
  useHumanReadableRange,
  useHumanReadableRanges,
} from "~/composables/useHumanReadableRanges";
import { useModalRouterUtils } from "~/composables/useModalRouterUtils";
import type { NoteData } from "~/components/commentBody.util.notes";
import type { ProcessedCommentaryComment } from "~/composables/processCommentarySearchResults/types";
import { useDarkMode } from "~/composables/useDarkMode";

type LanguageItemType = {
  code: string;
  name: string | undefined;
  to: RouteLocationGeneric | undefined;
  isCurrent: boolean;
};

const i18n = useI18n();
const localePath = useLocalePath();
const route = useRoute();
const dark = useDarkMode();

const scriptureClient = injectRequired(ScriptureClientKey);
const log = injectRequired(LogFunctionKey);

const props = withDefaults(defineProps<Props>(), {
  contributionsByRole: () => ({}),
  ranges: () => [],
});

const insideModal = inject(InModalViewKey, false);
const { forceCleanNuxtPagePlusModalHistoryStack, modalRouter } =
  useModalRouterUtils();

function modalCloseHook(event: MouseEvent | TouchEvent | KeyboardEvent) {
  if (event.ctrlKey || event.shiftKey || event.metaKey || event.altKey) {
    return;
  }
  if (insideModal) {
    event.preventDefault();
    forceCleanNuxtPagePlusModalHistoryStack();
  }
}

function modalLanguageHook(
  event: MouseEvent | TouchEvent | KeyboardEvent,
  languageItem: LanguageItemType,
) {
  if (event.ctrlKey || event.shiftKey || event.metaKey || event.altKey) {
    return;
  }
  if (insideModal && languageItem.to) {
    event.stopImmediatePropagation();
    event.preventDefault();
    event.stopPropagation();
    const payloadQueryLanguage =
      languageItem.to?.query?.language?.toString() ?? undefined;
    modalRouter.replace({ query: { language: payloadQueryLanguage } });
  }
}

// TODO: Do something with the error from this query.
const humanReadableRangesQuery = useHumanReadableRanges(
  scriptureClient,
  log,
  computed(() => props.ranges),
  computed(() => props.localTid),
  false,
  false,
);

const pageTitle = computed<string>(() => {
  const explicitTitle = props.heading || props.group;
  if (explicitTitle) {
    return explicitTitle;
  }

  const refsList = makeListString(
    humanReadableRangesQuery.data.value?.value ?? [],
  );
  return refsList
    ? `${props.commentaryType.displayName} for ${refsList}`
    : props.commentaryType.displayName;
});

const documentTitle = computed<string>(() => {
  const title = `${humanReadableRangesQuery.data.value?.value?.[0] ?? "?"} > ${props.commentaryType.displayName}`;

  const explicitTitle = props.heading || props.group;
  if (explicitTitle) {
    return `${title} > ${explicitTitle}`;
  }

  return title;
});

const config = useRuntimeConfig();

const commentaryFiltersComputed = computed<CommentaryFilters>(() => {
  // For filters, fall back to "exposition" rather than "unassigned" or "unknown".
  return computeCommentaryFilters(
    props.commentaryFilters ?? {},
    props.ranges,
    props.commentaryType.machineName,
    props.detailLevelWeight,
    config.public.commentary.defaultVids,
  );
});

/**
 * The range of the commentary page to which we should link from this comment.
 */
const commentaryRange = computed<VidRange>(
  () =>
    parseVidsString(commentaryFiltersComputed.value.vids, null)[0] ||
    null ||
    props.ranges[0] ||
    makeRange(181, 181), // Should almost never happen.
);

// TODO: Do something with the error from this query.
const renderedBreadcrumbReferenceHtml = useHumanReadableRange(
  scriptureClient,
  log,
  commentaryRange,
  computed(() => props.localTid),
  true,
  false,
);

// TODO: Do something with the error from this query.
const renderedBreadcrumbReferencePlain = useHumanReadableRange(
  scriptureClient,
  log,
  commentaryRange,
  computed(() => props.localTid),
  true,
  true,
);

/**
 * The array of links to display at the very top of the page, indicating the logical "parents" of the current page.
 */
const breadcrumbs = computed<
  {
    to: SimpleNamedLocation;
    title: string;
    plainTitle?: string;
  }[]
>(() => [
  {
    to: getBibleLocationFromRange(commentaryRange.value),
    title: renderedBreadcrumbReferenceHtml.data.value?.value ?? "…",
    plainTitle: renderedBreadcrumbReferencePlain.data.value?.value ?? "…",
  },
  {
    to: getCommentaryLocationFromFilters(
      commentaryFiltersComputed.value,
      i18n.locale.value,
    ),
    title: getCommentaryNameForBreadcrumb(
      commentaryFiltersComputed.value,
      i18n.locale.value,
    ),
  },
]);

const parentCrumb = computed<
  | {
      to: SimpleNamedLocation;
      title: string;
      plainTitle?: string;
    }
  | undefined
>(() => breadcrumbs.value.slice(-1)[0]);

/**
 * JSON LD for structured data.
 *
 * See https://medium.com/@fmoessle/json-ld-with-vue-js-72cf1ac2af66
 */
const articleJsonLd = computed(() => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: props.heading,
  datePublished: props.created?.toDateString(), // FIXME: This is not really the published date. Maybe rather omit it?
  dateModified: props.changed?.toDateString(),
}));

const window = injectRequired(WindowKey);
const commentBody = computed(() =>
  prepareCommentBody(
    window,
    props.nid,
    props.body.kohaMentions,
    props.body.full,
    props.body.summary,
    1,
  ),
);

const showInfoPanel = ref<boolean>(false);

const bottomSheetNoteNumber = ref<number | null>(null);

const showBottomSheet = computed<boolean>({
  get() {
    return bottomSheetNoteNumber.value !== null;
  },
  set(value) {
    if (!value) {
      bottomSheetNoteNumber.value = null;
    }
  },
});

const bottomSheetNote = computed<NoteData | undefined>(() =>
  bottomSheetNoteNumber.value
    ? commentBody.value.notes[bottomSheetNoteNumber.value - 1]
    : undefined,
);

useHead({
  title: documentTitle,
  meta: [
    {
      name: "og:title",
      content: pageTitle,
    },
    {
      name: "og:description",
      content: documentTitle,
    },
  ],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify(articleJsonLd.value),
    },
  ],
});

const languageListItems = computed(() =>
  locales
    .filter((l) => props.availableLanguages.includes(l.code))
    .map((l): LanguageItemType => {
      // For showing a comment in a language other than the interface language, include the `language` query parameter.
      // Otherwise omit it.
      const languageQueryValue =
        i18n.locale.value === l.code ? undefined : l.code;
      const to: any = {
        query: { language: languageQueryValue },
      };
      return {
        code: l.code,
        name: l.name,
        to,
        isCurrent: l.code === props.language,
      };
    }),
);

onServerPrefetch(async () => {
  await humanReadableRangesQuery.suspense();
  await renderedBreadcrumbReferenceHtml.suspense();
  await renderedBreadcrumbReferencePlain.suspense();
});

// TODO: Implement previous and next for the case where the previous or next result is an image.
const previousComment = computed<ProcessedCommentaryComment | undefined>(() =>
  props.previousResult?.type === "comment" ? props.previousResult : undefined,
);
const nextComment = computed<ProcessedCommentaryComment | undefined>(() =>
  props.nextResult?.type === "comment" ? props.nextResult : undefined,
);
</script>
